import type {
  AgentCommissionRule,
  Booking,
  CommissionBreakdown,
  PaymentSchedule,
  UnitStatus,
} from "@/types/realEstate";

/** Default Construction_Linked plan template (percentages must sum to 100). */
export const CONSTRUCTION_LINKED_MILESTONES: Array<{
  milestone_name: string;
  due_percentage: number;
}> = [
  { milestone_name: "On Booking Token", due_percentage: 10 },
  { milestone_name: "On Foundation & Plinth", due_percentage: 20 },
  { milestone_name: "On Mid-Structure Slab", due_percentage: 15 },
  { milestone_name: "On Handover & Possession", due_percentage: 55 },
];

const MILESTONE_DUE_DAYS: Record<string, number> = {
  "On Booking Token": 0,
  "On Foundation & Plinth": 90,
  "On Mid-Structure Slab": 180,
  "On Handover & Possession": 365,
};

function isoDateFromToday(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function buildConstructionLinkedSchedule(
  bookingId: string,
  agreementValue: number,
): Omit<PaymentSchedule, "id" | "created_at">[] {
  const safeAgreement = Number(agreementValue);
  const base = Number.isFinite(safeAgreement) ? safeAgreement : 0;
  return CONSTRUCTION_LINKED_MILESTONES.map((m) => {
    const amountDue = Math.round((base * m.due_percentage) / 100);
    return {
      booking_id: bookingId,
      milestone_name: m.milestone_name,
      due_percentage: m.due_percentage,
      amount_due: amountDue,
      amount_paid: 0,
      due_date: isoDateFromToday(MILESTONE_DUE_DAYS[m.milestone_name] ?? 0),
      status: "PENDING" as const,
    };
  });
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(id: unknown): boolean {
  return typeof id === "string" && UUID_RE.test(id);
}

/** Allowed unit status edges — single source of truth for Wave 4. */
export const UNIT_STATUS_TRANSITIONS: Record<UnitStatus, UnitStatus[]> = {
  Available: ["On_Hold", "Token_Received", "Booked"],
  On_Hold: ["Available", "Token_Received", "Booked"],
  Token_Received: ["Booked", "Available", "On_Hold"],
  // Booked → Available is NOT a general edge; only cancelBooking may force it.
  Booked: ["Sold", "Token_Received"],
  Sold: [],
};

/** Default annual late-payment interest rate (%). Overridable in Settings. */
export const DEFAULT_LATE_INTEREST_ANNUAL_PERCENT = 12;

export type CancellationRefundLedger = {
  amountPaidTotal: number;
  deductionPercent: number;
  deductionAmount: number;
  refundAmount: number;
};

/** Refund = paid − forfeit; deductionPercent is 0–100 of amount paid. */
export function computeCancellationRefund(
  amountPaidTotal: number,
  deductionPercent: number,
): CancellationRefundLedger {
  const paid = Math.max(0, Number(amountPaidTotal) || 0);
  const pct = Math.min(100, Math.max(0, Number(deductionPercent) || 0));
  const deductionAmount = Math.round((paid * pct) / 100);
  return {
    amountPaidTotal: paid,
    deductionPercent: pct,
    deductionAmount,
    refundAmount: Math.max(0, paid - deductionAmount),
  };
}

/**
 * Simple interest on outstanding principal: amount × (annual%/100) × days/365.
 * Returns 0 when not overdue or inputs invalid.
 */
export function computeLateInterest(input: {
  outstanding: number;
  annualPercent: number;
  dueDate?: string | null;
  asOf?: Date;
}): number {
  const principal = Number(input.outstanding);
  const rate = Number(input.annualPercent);
  if (!Number.isFinite(principal) || principal <= 0) return 0;
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  if (!input.dueDate) return 0;

  const due = new Date(input.dueDate);
  if (Number.isNaN(due.getTime())) return 0;
  const asOf = input.asOf ?? new Date();
  // Compare calendar dates in local time
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const asOfDay = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
  const daysLate = Math.floor(
    (asOfDay.getTime() - dueDay.getTime()) / (24 * 60 * 60 * 1000),
  );
  if (daysLate <= 0) return 0;
  return Math.round((principal * (rate / 100) * daysLate) / 365);
}

export function normalizeLateInterestAnnualPercent(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return DEFAULT_LATE_INTEREST_ANNUAL_PERCENT;
  if (n > 100) return 100;
  return n;
}

export type CanTransitionUnitStatusOptions = {
  /** Set only by cancelBooking — releases Booked → Available. */
  allowBookingCancel?: boolean;
};

export function canTransitionUnitStatus(
  from: UnitStatus,
  to: UnitStatus,
  options?: CanTransitionUnitStatusOptions,
): boolean {
  if (from === to) return true;
  if (
    from === "Booked" &&
    to === "Available" &&
    options?.allowBookingCancel === true
  ) {
    return true;
  }
  return UNIT_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

/** Deal stage to set when unit reaches this status (null = no change). */
export function unitStatusToDealStage(status: UnitStatus): string | null {
  switch (status) {
    case "On_Hold":
      return "negotiation";
    case "Token_Received":
      return "token";
    case "Booked":
      return "booking";
    case "Sold":
      return "won";
    default:
      return null;
  }
}

export const HOLD_UNTIL_MARKER_PREFIX = "[hold_until:";

export function formatHoldUntilMarker(iso: string): string {
  return `${HOLD_UNTIL_MARKER_PREFIX}${iso}]`;
}

export function parseHoldUntilFromNotes(notes?: string | null): string | null {
  if (!notes) return null;
  const m = notes.match(/\[hold_until:([^\]]+)\]/);
  return m?.[1] ?? null;
}

/** Prefer the trailing `Note: …` segment from Match Lead deal descriptions. */
export function extractMatchNote(
  description?: string | null,
): string | undefined {
  if (!description?.trim()) return undefined;
  const noteOnly = description.match(/(?:^|[·|]\s*)Note:\s*(.+)$/i);
  if (noteOnly?.[1]?.trim()) return noteOnly[1].trim();
  return description.trim();
}

/**
 * Manual Unlink Lead is only for match/hold (deal) links.
 * Active bookings / Booked|Sold units must use Cancel Booking (or stay linked).
 */
export function unlinkLeadBlockedReason(
  status: UnitStatus,
  hasActiveBooking: boolean,
): string | null {
  if (hasActiveBooking || status === "Booked") {
    return "This unit has an active booking. Cancel the booking first to unlink the lead.";
  }
  if (status === "Sold") {
    return "Sold units cannot be unlinked.";
  }
  return null;
}

export function canUnlinkLeadFromUnit(
  status: UnitStatus,
  hasActiveBooking: boolean,
): boolean {
  return unlinkLeadBlockedReason(status, hasActiveBooking) == null;
}

/** Hold exclusivity: when enabled, only holder or manager may change an On_Hold unit. */
export function canModifyUnitHold(input: {
  restrictHoldChangesToHolder?: boolean;
  heldBy?: string | number | null;
  currentSalesId?: string | number | null;
  isManager?: boolean;
}): boolean {
  if (input.restrictHoldChangesToHolder === false) return true;
  if (input.isManager) return true;
  // Legacy On_Hold rows with no held_by: admin-only (do not let any agent steal).
  if (input.heldBy == null || input.heldBy === "") return false;
  if (input.currentSalesId == null) return false;
  return String(input.heldBy) === String(input.currentSalesId);
}

export const HOLD_RESTRICTED_ERROR =
  "Only the holding agent or a manager can change this hold.";

export function defaultHoldUntilIso(hours = 48): string {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

export const DEFAULT_AGENT_COMMISSION_RULE = {
  type: "percent",
  rate: 0.5,
} satisfies AgentCommissionRule;

/** Percentage (not fraction) of agreement value earned by the internal agent. */
export function resolveAgentCommissionRate(
  rule: AgentCommissionRule | undefined,
  agreementValue: number,
): number {
  const value = Number(agreementValue);
  if (!Number.isFinite(value) || value < 0) return 0;

  const effectiveRule = rule ?? DEFAULT_AGENT_COMMISSION_RULE;

  if (effectiveRule.type === "percent") {
    const rate = Number(effectiveRule.rate);
    return Number.isFinite(rate) && rate >= 0 ? rate : 0;
  }

  const slabs = effectiveRule.slabs;
  if (!slabs?.length) return 0;

  const validSlabs = slabs.filter(
    (s) =>
      Number.isFinite(Number(s.minAgreementValue)) &&
      Number.isFinite(Number(s.rate)),
  );
  if (!validSlabs.length) return 0;

  const qualifying = validSlabs.filter(
    (s) => value >= Number(s.minAgreementValue),
  );
  // Below every threshold means no slab was earned — never fall back to a higher tier.
  if (!qualifying.length) return 0;

  const picked = qualifying.reduce((best, s) =>
    Number(s.minAgreementValue) >= Number(best.minAgreementValue) ? s : best,
  );

  const rate = Number(picked.rate);
  return Number.isFinite(rate) && rate >= 0 ? rate : 0;
}

export function computeCommissions(input: {
  agreementValue: number;
  cpPercentage?: number | null;
  agentRule?: AgentCommissionRule;
}): CommissionBreakdown {
  const agreementValue = Number(input.agreementValue);
  const safeAgreement =
    Number.isFinite(agreementValue) && agreementValue >= 0 ? agreementValue : 0;

  const cpPct = Number(input.cpPercentage ?? 0);
  const safeCpPct = Number.isFinite(cpPct) && cpPct >= 0 ? cpPct : 0;

  const agentRate = resolveAgentCommissionRate(input.agentRule, safeAgreement);

  return {
    cpPercentage: safeCpPct,
    cpAmount: Math.round((safeAgreement * safeCpPct) / 100),
    agentRate,
    agentAmount: Math.round((safeAgreement * agentRate) / 100),
  };
}

/** True when the booked value is below list price by more than tolerance (default 0). */
export function requiresDiscountApproval(
  listPrice: number | null | undefined,
  agreementValue: number,
  tolerance = 0,
): boolean {
  if (listPrice == null) return false;

  const list = Number(listPrice);
  const agreement = Number(agreementValue);
  const tol = Number(tolerance);
  if (!Number.isFinite(list) || !Number.isFinite(agreement)) return false;
  if (list < 0 || agreement < 0) return false;

  const safeTol = Number.isFinite(tol) && tol >= 0 ? tol : 0;
  return agreement < list - safeTol;
}

export const REJECT_DISCOUNT_PAYMENT_BLOCK_ERROR =
  "Cannot reject: payments already recorded";

type SchedulePaymentCheck = Pick<PaymentSchedule, "amount_paid" | "status">;

/** True when collected money or paid milestones would be rewritten on reject. */
export function paymentSchedulesBlockDiscountReject(
  schedules: SchedulePaymentCheck[],
): boolean {
  return schedules.some(
    (s) => (Number(s.amount_paid) || 0) > 0 || s.status === "PAID",
  );
}

export type RejectedDiscountPatch = Pick<
  Booking,
  | "agreement_value"
  | "discount_amount"
  | "discount_status"
  | "discount_approved_by"
  | "cp_commission_amount"
  | "agent_commission_amount"
>;

/** Booking patch when a pending discount is rejected — restores list price and commissions. */
export function buildRejectedDiscountPatch(input: {
  booking: Pick<Booking, "list_price" | "agreement_value" | "cp_percentage">;
  agentRule?: AgentCommissionRule;
  salesId: string | number | null;
}): { patch: RejectedDiscountPatch; restoredAgreementValue: number } {
  const list = Number(input.booking.list_price);
  const currentAgreement = Number(input.booking.agreement_value) || 0;
  const restoredAgreementValue =
    input.booking.list_price != null && Number.isFinite(list) && list > 0
      ? list
      : currentAgreement;

  const commissions = computeCommissions({
    agreementValue: restoredAgreementValue,
    cpPercentage: input.booking.cp_percentage ?? 0,
    agentRule: input.agentRule,
  });

  return {
    restoredAgreementValue,
    patch: {
      agreement_value: restoredAgreementValue,
      discount_amount: 0,
      discount_status: "rejected",
      discount_approved_by: input.salesId,
      cp_commission_amount: commissions.cpAmount,
      agent_commission_amount: commissions.agentAmount,
    },
  };
}
